
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, debounceTime, map, Observable, of, Subject } from 'rxjs';
import { Router } from '@angular/router';

import { environment } from 'src/environments/environment';
import { AuthService } from 'src/app/core/services/auth.service';
import { MessageService } from 'src/app/core/services/message.service';


@Injectable({
  providedIn: 'root'
})
export class SharedUtilsService {

  public rechercheSubject = new Subject<string>();

  adresse: string = '';
  latitude: number = 0.0;
  longitude: number= 0.0;
  suggestions: any[] = [];

  estEnChargement: boolean = false;

  constructor(private http: HttpClient
    , private authService: AuthService
    , private messageService: MessageService
    , private router: Router
  ) {
    this.obtenirLocalisationUtilisateur();
      this.rechercheSubject.pipe(debounceTime(400)).subscribe((valeur) => {
      this.recupererSuggestions(valeur);
    });
  }

  obtenirLocalisationUtilisateur() {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((position) => {
        this.latitude = position.coords.latitude;
        this.longitude = position.coords.longitude;
      });
    }
  }

  recupererSuggestions(requete: string) {
    if (!requete || requete.length < 3 || !this.latitude || !this.longitude) {
      this.suggestions = [];
      return;
    }

    const encode = encodeURIComponent(requete);
    const lat = this.latitude;
    const lng = this.longitude;
    const delta = 0.1;
    const zoneVue = `${lng - delta},${lat - delta},${lng + delta},${lat + delta}`;
    const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encode}&addressdetails=1&limit=5&viewbox=${zoneVue}&bounded=1`;

    this.estEnChargement = true;

    this.http.get<any[]>(url).subscribe({
      next: (resultats) => {
        this.suggestions = resultats;
        this.estEnChargement = false;
      },
      error: () => this.suggestions = []
    });
  }

  resoudreAdresseVersCoordonnees(adresse: string): Observable<{ lat: number; lng: number } | null> {
    const requete = (adresse || '').trim();
    if (!requete || requete.length < 3) {
      return of(null);
    }
    const encode = encodeURIComponent(requete);
    const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encode}&addressdetails=1&limit=1`;
    return this.http.get<any[]>(url).pipe(
      map((resultats) => {
        const premier = Array.isArray(resultats) && resultats.length > 0 ? resultats[0] : null;
        if (!premier) return null;
        const lat = Number(premier.lat);
        const lng = Number(premier.lon);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
        return { lat, lng };
      }),
      catchError(() => of(null))
    );
  }

  obtenirValeurDepuisObjet<T extends Record<string, any>>(obj: T, champ: string): any {
    if(champ == null) champ = '';
    return obj[champ];
  }

  arrondirXtainesSuperieures(nombre: number, xTaine: number): number {
    return Math.ceil(nombre / xTaine) * xTaine;
  }

  obtenirListeDonnees(donnees: any) {
    if(donnees.dataName == 'Expéditeur' || donnees.dataName == 'Destinataire') {
      return this.obtenirContactsEnregistres(donnees.dataName);
    } else if(donnees.dataName == 'LivreursDisponibles') {
      return this.obtenirLivreursDisponibles(donnees.dataId);
    } else if(donnees.dataName == 'Zone') {
      return this.obtenirZonesActives();
    } else if(donnees.dataName == 'TrajetsParZone') {
      return this.obtenirTrajetsActifs();
    } else {
      return this.http.get('');
    }
  }

  obtenirContactsEnregistres(typeContact: string) {
    let typeContactURL = '';
    let idUtilisateur = this.authService.infosUtilisateur.userid;
    let url = '';

    if(typeContact == 'Expéditeur') {
      typeContactURL = 'expediteurs/user/' + idUtilisateur;
    } else {
      typeContactURL = 'destinataires/user/' + idUtilisateur;
    }
    url = `${environment.apiurl}` + typeContactURL;

    return this.http.get(url);
  }

  obtenirLivreursDisponibles(idCommande: number) {
    const url = `${environment.apiurl}` + 'users/livreurs/proches/' + idCommande;
    return this.http.get(url);
  }

  obtenirZonesActives(): Observable<any> {
    const url = `${environment.apiurl}` + 'zones/actives';
    return this.http.get<any>(url).pipe(
      catchError(this.messageService.gererErreur)
    );
  }

  obtenirTrajetsActifs(): Observable<any> {
    const url = `${environment.apiurl}trajet/actifs`;
    return this.http.get<any>(url).pipe(
      map((reponse) => {
        const liste = Array.isArray(reponse) ? reponse : (reponse?.content ?? []);
        return liste.filter((t: any) => t.actif === true);
      }),
      catchError(this.messageService.gererErreur)
    );
  }

  attribuerLivreurManuellement(idCommande: number, idLivreur: number, montant?: number): Observable<any> {
    let url = `${environment.apiurl}commandes/${idCommande}/attribuer-livreur-manuel?livreurId=${idLivreur}`;
    if (montant != null) url += `&montant=${montant}`;
    return this.http.put(url, undefined).pipe(
      catchError(this.messageService.gererErreur)
    );
  }

  supprimerCommande(idCommande: number): Observable<any> {
    const url = `${environment.apiurl}` + `commandes/supprimerCommande/${idCommande}`;
    return this.http.delete(url).pipe(
      catchError(this.messageService.gererErreur)
    );
  }

  numberOnly(event : any): boolean {
    const charCode = (event.which) ? event.which : event.keyCode;
    if (charCode > 31 && (charCode < 48 || charCode > 57)) {
      return false;
    }
    return true;
  }
}
